import { lazy, Suspense, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, Phone, VideoCamera, X } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { Toaster } from "@/components/ui/sonner";
import { captureSentryException } from "@/lib/sentry";
import { openExternalUrl } from "@/lib/open-url";
import { parseCallWindowPayload } from "./call-window-params";

const CallSurface = lazy(() => import("./CallSurface"));

export function CallWindowPage() {
  const { payload, error } = useMemo(
    () =>
      parseCallWindowPayload(window.location.search || window.location.hash),
    [],
  );

  const handleClose = useCallback(() => {
    window.close();
  }, []);

  const handleError = useCallback(
    (err: Error) => {
      captureSentryException(err, { tag: "livekit-call-window" });
      toast.error("Couldn't connect to the call window.", {
        action: payload?.joinUrl
          ? {
              label: "Open in browser",
              onClick: () => openExternalUrl(payload.joinUrl),
            }
          : undefined,
      });
    },
    [payload?.joinUrl],
  );

  if (!payload) {
    return (
      <div className="flex h-screen items-center justify-center bg-background px-4 text-foreground">
        <Toaster />
        <div className="w-full max-w-lg rounded-xl border border-border bg-background p-6 shadow-lg">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-lg font-semibold">Call window unavailable</h1>
              <p className="text-sm text-muted-foreground">
                {error ?? "The window could not be initialized."}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={handleClose}
              title="Close window"
            >
              <X size={14} />
            </Button>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClose}>
              Close window
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const modeLabel = payload.mode === "audio" ? "Audio" : "Video";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <Toaster />
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-border bg-background/95 px-3 pl-20 [-webkit-app-region:drag]">
        <div className="flex min-w-0 items-center gap-2">
          {payload.mode === "audio" ? (
            <Phone size={14} className="shrink-0 text-success" />
          ) : (
            <VideoCamera size={14} className="shrink-0 text-success" />
          )}
          <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">
            {modeLabel} call · #{payload.channelName}
          </p>
        </div>
        <div className="flex items-center gap-0.5 [-webkit-app-region:no-drag]">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => openExternalUrl(payload.joinUrl)}
            title="Open in browser"
          >
            <ArrowSquareOut size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={handleClose}
            title="Close window"
          >
            <X size={14} />
          </Button>
        </div>
      </header>
      <main className="min-h-0 flex-1 bg-overlay/50">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <LoadingIcon size={16} />
              Connecting…
            </div>
          }
        >
          <CallSurface
            serverUrl={payload.serverUrl}
            token={payload.token}
            mode={payload.mode}
            onDisconnected={handleClose}
            onError={handleError}
          />
        </Suspense>
      </main>
    </div>
  );
}

export default CallWindowPage;
